import React, { useEffect, useRef, useState } from "react";
import { C } from "../../../constants/design";
import type { MatchState, Side, DuelPlayer, AvatarLayers, MatchEventType } from "../types/leagueDuel";
import { stepMatch } from "../engine/simulation";
import { LayeredCharacter } from "./LayeredCharacter";
import { useI18n } from "../../../i18n/I18nContext";
import { PP_IMGS, LAYER_IMGS, STATS } from "../../../constants/items";
import { StatIcon } from "../../../components/ui/StatIcon";
import type { EquipmentStats } from "../engine/combat";

// ─── Écran de match ───────────────────────────────────────────────────────────
// Mise en scène : duellistes (avatars équipés) face à face, possession, phases,
// événements + petits messages de succès avec lueur verte, feed, CTA "Passer".

const TICK_MS = 1400;

const SUCCESS_EVENT_TYPES: MatchEventType[] = ["goal", "save", "interception", "shot"];

export function LeagueDuelMatch({
  initial,
  onFinish,
  homeLayers,
  awayLayers,
  playerStats,
  opponentStats,
}: {
  initial: MatchState;
  onFinish: (final: MatchState) => void;
  homeLayers?: AvatarLayers;
  awayLayers?: AvatarLayers;
  playerStats?: EquipmentStats;
  opponentStats?: EquipmentStats;
}) {
  const { t } = useI18n();
  const [state, setState] = useState<MatchState>(initial);
  const stateRef = useRef(state);
  stateRef.current = state;
  const skippedRef = useRef(false);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const successMessage = (type: MatchEventType): string | undefined => {
    if (type === "goal") return t("leagueDuel.eventGoal");
    if (type === "save") return t("leagueDuel.eventSave");
    if (type === "interception") return t("leagueDuel.eventInterception");
    if (type === "shot") return t("leagueDuel.eventShot");
    return undefined;
  };

  const [flash, setFlash] = useState<{ side: Side; message: string; tone?: "critical" } | null>(null);
  const lastFlashedId = useRef<number>(-1);
  const flashTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Cleanup on unmount only — not on each re-run, so StrictMode doesn't clear the timer mid-flash.
  useEffect(() => () => { if (flashTimerRef.current) clearTimeout(flashTimerRef.current); }, []);

  useEffect(() => {
    if (state.finished) {
      const t = setTimeout(() => {
        if (!skippedRef.current) onFinish(stateRef.current);
      }, 900);
      return () => clearTimeout(t);
    }
    intervalRef.current = setInterval(() => setState((s) => stepMatch(s)), TICK_MS);
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [state.finished]); // eslint-disable-line

  // Détecte le dernier événement pour déclencher un flash de succès.
  const last = state.events[0];
  useEffect(() => {
    if (!last || last.id === lastFlashedId.current) return;
    let msg: string | undefined;
    if (last.tone === "critical") {
      msg = last.label; // "Critical Interception" or "Critical Pass"
    } else if (SUCCESS_EVENT_TYPES.includes(last.type)) {
      msg = successMessage(last.type);
    }
    if (msg) {
      lastFlashedId.current = last.id;
      if (flashTimerRef.current) clearTimeout(flashTimerRef.current);
      setFlash({ side: last.side, message: msg, tone: last.tone });
      flashTimerRef.current = setTimeout(() => setFlash(null), 1000);
    }
  }, [last?.id]); // eslint-disable-line

  // CTA "Passer" : déroule la sim jusqu'à la fin instantanément.
  const skipToEnd = () => {
    const startedAt = performance.now();
    console.log("[SKIP] click");

    if (skippedRef.current) {
      console.log("[SKIP] ignored — already skipped");
      return;
    }
    skippedRef.current = true;

    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
      console.log("[SKIP] interval cleared");
    }

    let s = stateRef.current;
    let guard = 0;
    while (!s.finished && guard++ < 200) s = stepMatch(s);

    console.log("[SKIP] simulation done", {
      elapsedMs: Math.round(performance.now() - startedAt),
      guard,
      finished: s.finished,
      phase: s.phase,
      score: `${s.homeScore}-${s.awayScore}`,
    });

    console.log("[SKIP] before onFinish");
    onFinish(s);
    console.log("[SKIP] after onFinish", Math.round(performance.now() - startedAt), "ms");
  };

  // Barre de buts : 0-0 → 50/50, sinon proportion du score (anim smooth via transition).
  const totalGoals = state.homeScore + state.awayScore;
  const homePct = totalGoals === 0 ? 50 : (state.homeScore / totalGoals) * 100;

  return (
    <div style={{ padding: 16, maxWidth: 860, margin: "0 auto", color: C.text }}>
      {/* Duellistes face à face : nous à gauche, adversaire à droite en miroir */}
      <div style={{ display: "flex", alignItems: "flex-end", justifyContent: "space-between", gap: 12, marginBottom: 14 }}>
        <DuelistPortrait
          name={state.home.name} side="home" layers={homeLayers}
          active={state.possession === "home"}
          flashMessage={flash?.side === "home" ? flash.message : undefined}
          flashTone={flash?.side === "home" ? flash.tone : undefined}
        />
        <div style={{ fontWeight: 900, fontSize: 24, color: C.faint, alignSelf: "center" }}>VS</div>
        <DuelistPortrait
          name={state.away.name} side="away" layers={awayLayers} mirror
          active={state.possession === "away"}
          flashMessage={flash?.side === "away" ? flash.message : undefined}
          flashTone={flash?.side === "away" ? flash.tone : undefined}
        />
      </div>

      {/* Barre de buts */}
      <div style={{ fontSize: 11, color: C.faint, textAlign: "center", marginBottom: 4 }}>
        {t("leagueDuel.goalsBar")}
      </div>
      <div style={{ display: "flex", height: 12, borderRadius: 6, overflow: "hidden", marginBottom: 12 }}>
        <div style={{ width: `${homePct}%`, background: C.turf, transition: "width .6s cubic-bezier(.22,1,.36,1)" }} />
        <div style={{ width: `${100 - homePct}%`, background: C.live, transition: "width .6s cubic-bezier(.22,1,.36,1)" }} />
      </div>

      {/* Stats delta — 4 pills montrant l'avantage/désavantage vs l'adversaire */}
      {(playerStats || opponentStats) && (
        <StatsDelta home={playerStats} away={opponentStats} />
      )}

      {/* Score + phase */}
      <div style={{ textAlign: "center", marginBottom: 12 }}>
        <div style={{ fontFamily: "monospace", fontWeight: 900, fontSize: 30 }}>
          {state.homeScore} – {state.awayScore}
        </div>
        <div style={{ fontSize: 12, color: C.faint }}>
          {state.finished ? t("leagueDuel.finished") : t("leagueDuel.phase", state.phase, state.totalPhases)}
        </div>
      </div>

      {/* Terrain : deux colonnes face à face */}
      <div
        style={{
          display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10,
          background: "linear-gradient(90deg,rgba(45,227,107,0.06),rgba(255,59,71,0.06))",
          border: `1px solid ${C.line}`, borderRadius: 14, padding: "14px 8px", minHeight: 220,
        }}
      >
        <TeamColumn team={state.home} side="home" state={state} lastEventSide={last?.side} lastFloating={last?.floatingLabel} lastFloatingCritical={last?.tone === "critical"} />
        <TeamColumn team={state.away} side="away" state={state} mirror lastEventSide={last?.side} lastFloating={last?.floatingLabel} lastFloatingCritical={last?.tone === "critical"} />
      </div>

      {/* CTA Passer — sticky, toujours visible même si le feed est long */}
      {!state.finished && (
        <div
          style={{
            position: "sticky", bottom: 57,
            padding: "8px 0 2px",
            background: "linear-gradient(to bottom, transparent, #080C14 40%)",
          }}
        >
          <button
            type="button"
            onClick={skipToEnd}
            style={{
              width: "100%", padding: "12px 0", borderRadius: 10,
              border: `1px solid ${C.line}`, background: C.surface, color: C.muted,
              fontWeight: 800, fontSize: 13, cursor: "pointer",
            }}
          >
            {t("leagueDuel.skipButton")}
          </button>
        </div>
      )}

      {/* Feed d'événements */}
      <div style={{ marginTop: 10 }}>
        <div style={{ fontSize: 11, fontWeight: 800, color: C.muted, marginBottom: 6 }}>{t("leagueDuel.eventsLabel")}</div>
        <div style={{ maxHeight: 160, overflowY: "auto" }}>
          {state.events.map((e) => (
            <div
              key={e.id}
              style={{
                fontSize: 12.5, padding: "5px 0", borderBottom: `1px solid ${C.lineSoft}`,
                color: e.type === "goal" ? C.turf : C.muted, fontWeight: e.type === "goal" ? 800 : 400,
              }}
            >
              {e.tone === "critical" ? (
                <span style={{
                  display: "inline-block",
                  background: "#2563eb", color: "#fff",
                  borderRadius: 999, padding: "2px 10px",
                  fontSize: 11, fontWeight: 800,
                }}>
                  {e.label}
                </span>
              ) : e.label}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function DuelistPortrait({
  name, side, layers, active, mirror, flashMessage, flashTone,
}: {
  name: string;
  side: Side;
  layers?: AvatarLayers;
  active: boolean;
  mirror?: boolean;
  flashMessage?: string;
  flashTone?: "critical";
}) {
  const teamColor = side === "home" ? C.turf : C.live;
  const flashColor = flashTone === "critical" ? "#2563eb" : teamColor;
  return (
    <div style={{ flex: 1, textAlign: "center", opacity: active ? 1 : 0.6, transition: "opacity .3s" }}>
      <div
        className={flashMessage ? "duel-lunge" : active ? "duel-bob" : undefined}
        style={{ position: "relative", display: "inline-block", width: "100%", maxWidth: 170 }}
      >
        {active && (
          <span style={{ position: "absolute", top: -14, left: "50%", transform: "translateX(-50%)", fontSize: 18, zIndex: 10 }}>⚽</span>
        )}
        {flashMessage && (
          <span
            style={{
              position: "absolute", top: -8, left: "50%", transform: "translateX(-50%)",
              whiteSpace: "nowrap", fontSize: 12, fontWeight: 900,
              color: flashTone === "critical" ? "#fff" : "#06121e",
              background: flashColor,
              border: flashTone === "critical" ? `2px solid #1d4ed8` : "none",
              borderRadius: 999, padding: "3px 10px", zIndex: 11,
              boxShadow: `0 2px 10px ${flashColor}88`,
            }}
          >
            {flashMessage}
          </span>
        )}
        <LayeredCharacter layers={layers} mirror={mirror} glowColor={flashMessage ? flashColor : undefined} />
      </div>
      <div style={{ fontSize: 13, fontWeight: 800, color: teamColor, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
        {name}
      </div>
    </div>
  );
}

// 4 pills : icône stat du profil + delta (player - opponent), vert si positif, rouge si négatif
function StatsDelta({ home, away }: { home?: EquipmentStats; away?: EquipmentStats }) {
  const h = home ?? { technique: 0, defense: 0, physique: 0, vitesse: 0 };
  const a = away ?? { technique: 0, defense: 0, physique: 0, vitesse: 0 };

  return (
    <div
      style={{
        display: "grid",
        gridTemplateColumns: "repeat(4, 1fr)",
        gap: 6,
        marginBottom: 10,
      }}
    >
      {STATS.map((st) => {
        const delta = h[st.key as keyof EquipmentStats] - a[st.key as keyof EquipmentStats];
        const positive = delta >= 0;
        const color = positive ? C.turf : C.live;
        return (
          <div
            key={st.key}
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: 6,
              background: C.surface,
              border: `1px solid ${C.line}`,
              borderRadius: 10,
              padding: "8px 4px",
            }}
          >
            <StatIcon st={st} size={22} />
            <span style={{ fontSize: 13, fontWeight: 800, color }}>
              {positive ? "+" : ""}{delta}
            </span>
          </div>
        );
      })}
    </div>
  );
}

function TeamColumn({
  team, side, state, mirror, lastEventSide, lastFloating, lastFloatingCritical,
}: {
  team: MatchState["home"];
  side: Side;
  state: MatchState;
  mirror?: boolean;
  lastEventSide?: Side;
  lastFloating?: string;
  lastFloatingCritical?: boolean;
}) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 8, transform: mirror ? "scaleX(-1)" : undefined }}>
      {team.players.map((p) => {
        const isActive = lastEventSide === side && state.events[0]?.playerId === p.id;
        return (
          <PlayerRow
            key={p.id}
            player={p}
            hasBall={state.ball.playerId === p.id}
            fatigue={state.fatigue[p.id] ?? 0}
            mirror={mirror}
            floating={isActive ? lastFloating : undefined}
            floatingCritical={isActive ? lastFloatingCritical : undefined}
          />
        );
      })}
    </div>
  );
}

function PlayerRow({
  player, hasBall, fatigue, mirror, floating, floatingCritical,
}: {
  player: DuelPlayer;
  hasBall: boolean;
  fatigue: number;
  mirror?: boolean;
  floating?: string;
  floatingCritical?: boolean;
}) {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 8, position: "relative" }}>
      <div style={{ position: "relative" }}>
        {hasBall && (
          <span style={{ position: "absolute", top: -12, left: "50%", transform: "translateX(-50%)", fontSize: 14 }}>⚽</span>
        )}
        {floating && (
          <span
            style={{
              position: "absolute", top: -26, left: "50%",
              transform: `translateX(-50%) ${mirror ? "scaleX(-1)" : ""}`,
              whiteSpace: "nowrap", fontSize: 10, fontWeight: 800,
              background: floatingCritical ? "#2563eb" : C.surface2,
              color: floatingCritical ? "#fff" : undefined,
              border: floatingCritical ? "none" : `1px solid ${C.line}`,
              borderRadius: 8, padding: "2px 6px", zIndex: 2,
            }}
          >
            {floating}
          </span>
        )}
        <div
          style={{
            width: 34, height: 34, borderRadius: "50%",
            background: hasBall ? "rgba(245,179,1,0.2)" : C.surface,
            border: `1.5px solid ${hasBall ? C.gold : C.line}`,
            display: "flex", alignItems: "center", justifyContent: "center",
            fontSize: 18, transform: mirror ? "scaleX(-1)" : undefined,
          }}
        >
          {(() => {
            const av = player.avatar;
            const url = av ? (PP_IMGS[av] ?? LAYER_IMGS[av] ?? null) : null;
            if (url) return <img src={url} alt="" style={{ width: "100%", height: "100%", objectFit: "cover", borderRadius: "50%" }} />;
            // emoji or single char → render as-is; multi-word string → it's a stale item name, use fallback
            if (!av || av.length > 4) return <span style={{ fontSize: 16 }}>👤</span>;
            return av;
          })()}
        </div>
      </div>
      <div style={{ flex: 1, minWidth: 0, transform: mirror ? "scaleX(-1)" : undefined }}>
        <div style={{ fontSize: 11, fontWeight: 700, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
          {player.name}{player.isCaptain ? " ©" : ""}
        </div>
        <div style={{ height: 3, borderRadius: 3, background: C.line, marginTop: 2 }}>
          <div style={{ width: `${fatigue}%`, height: "100%", background: fatigue > 70 ? C.live : C.muted, borderRadius: 3 }} />
        </div>
      </div>
    </div>
  );
}
